/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RuleWorkflowBuilder.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useReactFlow } from "reactflow";
import FlowBuilder from "../../../components/flow-builder/FlowBuilder";
import { FullscreenExit } from "@mui/icons-material";
import { convertNeuDSLToGraph } from "../../../components/flow-builder/engine/convertNeuDSLToGraph";

const RuleWorkflowBuilder = forwardRef(function RuleWorkflowBuilder(
  {
    ruleSetId,
    onFullscreen,
    fullScreenMode,
    initialWorkflow,
    initialWorkflowId,
  },
  ref,
) {
  const saveRef = useRef(null);
  const loadRef = useRef(null);
  const { fitView } = useReactFlow();

  const [graphReady, setGraphReady] = useState(false);

  function loadWorkflowIntoGraph(workflow) {
    const graph = convertNeuDSLToGraph(workflow);

    if (!loadRef.current) {
      return;
    }
    loadRef.current(graph);
  }

  useImperativeHandle(ref, () => ({
    async saveWorkflow() {
      if (!saveRef.current) {
        return { success: false, error: "Workflow not ready" };
      }
      return await saveRef.current();
    },
  }));

  useEffect(() => {
    const t = setTimeout(() => {
      fitView({ padding: 0.2 });
    }, 50);

    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    console.log("RuleWorkflowBuilder mounted");
    return () => console.log("RuleWorkflowBuilder unmounted");
  }, []);

  useEffect(() => {

    if (!initialWorkflow) return;
    if (!graphReady) return;
    loadWorkflowIntoGraph(initialWorkflow);
  }, [initialWorkflow, graphReady]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          paddingBottom: 2,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {onFullscreen && (
          <Tooltip
            title={fullScreenMode ? "Minimize" : "Maximize"}
            arrow
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#000", // pure black tooltip
                  color: "#fff",
                  borderRadius: 0,
                  boxShadow: 3,
                  p: 1,
                  "& .MuiTooltip-arrow": {
                    color: "#000",
                  },
                },
              },
            }}
          >
            <IconButton
              size="small"
              onClick={onFullscreen}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                ml: 1,
              }}
            >
              {fullScreenMode ? <FullscreenExit /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <FlowBuilder
          ruleSetId={ruleSetId}
          registerSave={(fn) => {
            saveRef.current = fn;
          }}
          registerLoad={(fn) => {
            loadRef.current = fn;
            setGraphReady(true);
          }}
          workflowId={initialWorkflowId}
        />
      </Box>
    </Box>
  );
});

export default RuleWorkflowBuilder;
